import moment from "moment";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDAvatar from "components/MDAvatar";
import MDBadge from "components/MDBadge";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Complaints components

import DataTable from "examples/Tables/DataTable";

import { getComplaintsList, getComplaintStats } from "actions/complaintActions";

function Complaints() {
  const dispatch = useDispatch();

  const statsComplaint = useSelector((state) => state.statsComplaint);
  const { complaintsStats } = statsComplaint;

  const listComplaint = useSelector((state) => state.listComplaint);
  const { complaintsList } = listComplaint;

  useEffect(() => {
    if (complaintsList?.length === 0) {
      dispatch(getComplaintsList());
    }
  }, [dispatch, complaintsList?.length]);

  useEffect(() => {
    if (complaintsStats?.length === 0) {
      dispatch(getComplaintStats());
    }
  }, [dispatch, complaintsStats?.length]);

  const Supplier = ({ image, name, email }) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDAvatar src={image} name={name} size="sm" />
      <MDBox>
        <MDTypography
          variant="button"
          fontWeight="medium"
          ml={1}
          lineHeight={1}
        >
          {name}
        </MDTypography>
        <MDBox mt={1.5}>
          <MDTypography variant="button" fontSize="small" ml={1} lineHeight={1}>
            {email}
          </MDTypography>
        </MDBox>
      </MDBox>
    </MDBox>
  );

  function renderRows(data) {
    const values = [];
    data?.forEach((item, index) => {
      const newElement = {
        client: (
          <Link to={`/complaints/${item?._id}`}>
            <Supplier
              key={index}
              image={item?.order?.requestedBy?.profile_image}
              name={item?.order?.requestedBy?.name}
              email={item?.order?.requestedBy?.email}
            />
          </Link>
        ),
        complaint: (
          <MDBox>
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {item?.complaintNumber}
            </MDTypography>
            <MDBox mt={1.5}>
              <MDTypography variant="button" fontSize="small" lineHeight={1}>
                Ksh. {item?.amount}
              </MDTypography>
            </MDBox>
          </MDBox>
        ),
        product: (
          <MDBox>
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {item?.order?.product?.productName}
            </MDTypography>
            <MDBox mt={1.5}>
              <MDTypography variant="button" fontSize="small" lineHeight={1}>
                Ksh. {item?.order?.product?.amount}
              </MDTypography>
            </MDBox>
          </MDBox>
        ),
        date: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {moment(item?.created_at).format("DD MMM YYYY, HH mm")}
          </MDTypography>
        ),
        status: (
          <MDBox width="8rem" textAlign="center">
            <MDBox ml={-1}>
              <MDBadge
                badgeContent={item?.status}
                color={
                  item?.status == "approved"
                    ? "success"
                    : item?.status == "rejected"
                    ? "error"
                    : "info"
                }
                variant="gradient"
                size="sm"
              />
            </MDBox>
          </MDBox>
        ),
      };
      values.push(newElement);
    });
    return values;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          {complaintsStats?.map((item, index) => (
            <>
              {item?.statuses?.status == "pending" && (
                <Grid key={index} item xs={12} md={6} lg={2}>
                  <MDBox mb={1.5}>
                    <ComplexStatisticsCard
                      color="info"
                      icon="priority_high"
                      title="Pending Complaints"
                      count={item.statuses.count}
                      percentage={{
                        color: "info",
                        amount: item.statuses.percentage.toFixed(1),
                        label: "%",
                      }}
                    />
                  </MDBox>
                </Grid>
              )}
              {item?.statuses?.status == "processing" && (
                <Grid key={index} item xs={12} md={6} lg={2}>
                  <MDBox mb={1.5}>
                    <ComplexStatisticsCard
                      color="primary"
                      icon="hourglass_bottom"
                      title="Processing Complaints"
                      count={item.statuses.count}
                      percentage={{
                        color: "primary",
                        amount: item.statuses.percentage.toFixed(1),
                        label: "%",
                      }}
                    />
                  </MDBox>
                </Grid>
              )}
              {item?.statuses?.status == "approved" && (
                <Grid item xs={12} md={6} lg={2}>
                  <MDBox mb={1.5}>
                    <ComplexStatisticsCard
                      color="success"
                      icon="done"
                      title="Approved Complaints"
                      count={item.statuses.count}
                      percentage={{
                        color: "success",
                        amount: item.statuses.percentage.toFixed(1),
                        label: "%",
                      }}
                    />
                  </MDBox>
                </Grid>
              )}
              {item?.statuses?.status == "rejected" && (
                <Grid item xs={12} md={6} lg={2}>
                  <MDBox mb={1.5}>
                    <ComplexStatisticsCard
                      color="dark"
                      icon="clear"
                      title="Rejected Complaints"
                      count={item.statuses.count}
                      percentage={{
                        color: "dark",
                        amount: item.statuses.percentage.toFixed(1),
                        label: "%",
                      }}
                    />
                  </MDBox>
                </Grid>
              )}
            </>
          ))}
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="error"
                icon="donut_large"
                title="Total Complaints"
                count={
                  complaintsStats?.length ? complaintsStats[0]?.totalCount : "0"
                }
                percentage={{
                  color: "error",
                  amount: "100",
                  label: "%",
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
        <Divider />
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={12}>
              <Card sx={{ height: "100%" }}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  pt={3}
                  px={2}
                >
                  <MDBox>
                    <MDTypography variant="body2" fontWeight="small" mb={2}>
                      # To add new complaint, start selection from desired
                      product.
                    </MDTypography>
                    <MDTypography
                      px={3}
                      variant="h6"
                      fontWeight="medium"
                      textTransform="capitalize"
                    >
                      All Complaints
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" alignItems="flex-start">
                    <MDBox color="text" mr={0.5} lineHeight={0}>
                      <Icon color="inherit" fontSize="small">
                        date_range
                      </Icon>
                    </MDBox>
                    <MDTypography
                      variant="button"
                      color="text"
                      fontWeight="regular"
                    ></MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox>
                  <DataTable
                    table={{
                      columns: [
                        {
                          Header: "client",
                          accessor: "client",
                          width: "25%",
                          align: "left",
                        },
                        {
                          Header: "complaint",
                          accessor: "complaint",
                          width: "20%",
                          align: "center",
                        },
                        {
                          Header: "product",
                          accessor: "product",
                          width: "20%",
                          align: "left",
                        },
                        {
                          Header: "date",
                          accessor: "date",
                          width: "15%",
                          align: "left",
                        },
                        {
                          Header: "status",
                          accessor: "status",
                          width: "20%",
                          align: "center",
                        },
                      ],

                      rows: renderRows(complaintsList),
                    }}
                    showTotalEntries={false}
                    isSorted={false}
                    noEndBorder
                    entriesPerPage={false}
                  />
                </MDBox>
              </Card>
            </Grid>
            {/* <Grid item xs={12} md={5}>
              <PendingComplaints />
            </Grid> */}
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Complaints;
